import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  ButtonToolbar,
  Form,
  FormInstance,
  IconButton,
  Input,
  InputProps,
  Panel,
  Placeholder,
  Schema,
  SelectPicker,
  Stack,
  Message,
} from 'rsuite';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Close } from '@rsuite/icons';
import { ItemDataType } from 'rsuite/esm/@types/common';
import _ from 'lodash';
import PageContainer from '../components/PageContainer';
import XMLTransformationPreview from '../components/XMLPreview/XMLTransformationPreview';
import XMLDOMPreview from '../components/XMLPreview/XMLDOMPreview';
import {
  Credentials,
  TransformationRequestDto,
  TransformationResponseDto,
  XsltTemplate,
} from '../services/ApiType';
import { showSuccessMessage } from '../components/Toaster';
import { getTransformation, saveTransformation } from '../services/TransformationsService';
import { getXsltTemplates } from '../services/XsltsService';

interface TransformationEdit {
  id: number;
  name: string;
  xmlUrl: string;
  xsltDefinition: string;
  username?: string;
  password?: string;
}

function isNonEmpty(text?: string): boolean {
  const normalized = _.trim(text as string);

  return !_.isEmpty(normalized);
}

function Header() {
  return (
    <Link to="/">
      <IconButton icon={<Close />} appereance="subtle" circle />
    </Link>
  );
}

const Textarea = React.forwardRef<HTMLTextAreaElement, InputProps>(
  // eslint-disable-next-line react/jsx-props-no-spreading
  (props, ref) => <Input {...props} as="textarea" ref={ref} />,
);

const initialTransformationEdit: TransformationEdit = {
  id: 0,
  name: '',
  xmlUrl: '',
  xsltDefinition: '',
  username: '',
  password: '',
};

function areBothOrNoneFilled(
  first?: string,
  second?: string,
): boolean {
  const isFirstFilled = isNonEmpty(first);
  const isSecondFilled = isNonEmpty(second);
  return (isFirstFilled && isSecondFilled) || (!isFirstFilled && !isSecondFilled);
}

const bothOrNoneMessage = 'Both username and password must be filled or left empty.';

function createBothOrNoneRule(
  getOtherField: (data: TransformationEdit) => string | undefined,
) {
  return Schema
    .Types
    .StringType()
    .addRule((currentValue: string, formData: TransformationEdit) => {
      const currentText = currentValue as string | undefined;
      const otherText = getOtherField(formData);
      return areBothOrNoneFilled(currentText, otherText);
    }, bothOrNoneMessage);
}

const usernamePasswordRule = createBothOrNoneRule((data) => data?.password);

const passwordUsernameRule = createBothOrNoneRule((data) => data?.username);

const model = Schema.Model({
  xmlUrl: Schema.Types.StringType().isRequired('XML url is required.'),
  name: Schema.Types.StringType().isRequired('Name is required.'),
  xsltDefinition: Schema.Types.StringType().isRequired('XSLT definition is required.'),
  username: usernamePasswordRule,
  password: passwordUsernameRule,
});

function mapXsltTemplates(xsltTemplates: XsltTemplate[]): ItemDataType[] {
  return xsltTemplates
    .map(xsltTemplate => ({
      label: xsltTemplate.name,
      value: xsltTemplate.name,
    }));
}

function mapCredentials(username?: string, password?: string): Credentials | undefined {
  const usernameFilled = isNonEmpty(username);
  const passwordFilled = isNonEmpty(password);

  if (usernameFilled && passwordFilled) {
    return {
      username: (username as string).trim(),
      password: (password as string).trim(),
    };
  }

  return undefined;
}

function mapTransformationEditFromTransformationResponse(
  transformation: TransformationResponseDto,
): TransformationEdit {
  return {
    id: transformation.id,
    name: transformation.name,
    xmlUrl: transformation.xmlUrl,
    xsltDefinition: transformation.xsltDefinition,
    username: '',
    password: '',
  };
}

function mapTransformationRequestFromTransformationEdit(
  form: TransformationEdit,
): TransformationRequestDto {
  const credentials = mapCredentials(form.username, form.password);

  return {
    name: form.name,
    xmlUrl: form.xmlUrl,
    xsltDefinition: form.xsltDefinition,
    credentials,
  };
}

function AddTransformationPage() {
  const [
    transformationEdit,
    setTransformationEdit,
  ] = useState<TransformationEdit>({ ...initialTransformationEdit });
  const [
    xsltTemplates,
    setXsltTemplates,
  ] = useState<XsltTemplate[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [originallyAuthenticated, setOriginallyAuthenticated] = useState<boolean>(false);
  const formRef = useRef<FormInstance>(null);

  const navigate = useNavigate();
  const { id } = useParams();

  const isEditing = !!id;

  function checkValidation() {
    return formRef.current?.check() ?? false;
  }

  async function fetchTransformation(): Promise<TransformationResponseDto | undefined> {
    if (!id) {
      return undefined;
    }

    try {
      return await getTransformation(Number(id));
    } catch {
      return undefined;
    }
  }

  async function fetchXsltTemplates(): Promise<XsltTemplate[]> {
    try {
      return await getXsltTemplates();
    } catch {
      return [];
    }
  }

  async function fetchData(): Promise<void> {
    setIsLoading(true);

    try {
      const [fetchedTransformation, fetchedXsltTemplates] = await Promise.all([
        fetchTransformation(),
        fetchXsltTemplates(),
      ]);

      if (fetchedTransformation) {
        setTransformationEdit(
          mapTransformationEditFromTransformationResponse(fetchedTransformation),
        );
        setOriginallyAuthenticated(fetchedTransformation.authenticated);
      }

      setXsltTemplates(fetchedXsltTemplates);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData().then();
  }, [id]);

  useEffect(() => {
    const isTransformationEditValid = checkValidation();

    if (isTransformationEditValid) {
      formRef.current?.cleanErrors();
    }

    setIsValid(isTransformationEditValid);
  }, [transformationEdit]);

  const shouldShowAuthenticationReminder = isEditing && originallyAuthenticated;

  const onSave = () => {
    saveTransformation(
      transformationEdit.id,
      mapTransformationRequestFromTransformationEdit(transformationEdit),
    ).then(() => {
      navigate('/');
      showSuccessMessage(isEditing ? 'Item was successfully edited.' : 'New item was successfully added.');
    });
  };

  function handleXsltDefinitionChange(name: string): void {
    const xsltTemplate = xsltTemplates.find(value => value.name === name);

    if (!xsltTemplate) {
      return;
    }

    setTransformationEdit({ ...transformationEdit, xsltDefinition: xsltTemplate.definition });
  }

  return (
    <PageContainer
      title={isEditing ? 'Edit transformation' : 'Add new transformation'}
      header={<Header />}
    >
      <Panel bordered>
        {
          isLoading
            ? <Placeholder.Paragraph rows={10} active />
            : (
              <Form
                formValue={transformationEdit}
                ref={formRef}
                onChange={(changed) => {
                  setTransformationEdit(changed as TransformationEdit);
                }}
                model={model}
                fluid
                autoComplete="off"
              >
                <Form.Group controlId="xml-url-1">
                  <Form.ControlLabel>XML url</Form.ControlLabel>
                  <Stack direction="column" alignItems="stretch" spacing={20}>
                    <Form.Control name="xmlUrl" />
                    <Form.Group>
                      {shouldShowAuthenticationReminder && (
                        <Message type="warning" showIcon style={{ marginBottom: 8 }}>
                          This XML was originally authenticated.
                          Please fill in authentication before saving to preserve functionality
                          or leave empty to remove authentication.
                        </Message>
                      )}
                      <Form.ControlLabel>Authentication (optional)</Form.ControlLabel>
                      <Stack direction="row" spacing={20} alignItems="flex-start">
                        <Form.Control
                          name="username"
                          placeholder="Username"
                          style={{ width: '50%' }}
                          autoComplete="off"
                        />
                        <Form.Control
                          name="password"
                          type="password"
                          placeholder="Password"
                          style={{ width: '50%' }}
                          autoComplete="new-password"
                        />
                      </Stack>
                    </Form.Group>
                    <XMLDOMPreview
                      url={transformationEdit.xmlUrl}
                      credentials={
                            mapCredentials(
                              transformationEdit.username,
                              transformationEdit.password,
                            )
                          }
                    />
                  </Stack>
                </Form.Group>
                <Form.Group controlId="transformation-name">
                  <Form.ControlLabel>Name</Form.ControlLabel>
                  <Form.Control name="name" />
                </Form.Group>
                <Form.Group controlId="xslt-templates">
                  <Form.ControlLabel>XSLT templates</Form.ControlLabel>
                  <Form.Control
                    name="xsltTemplate"
                    block
                    searchable={false}
                    accepter={SelectPicker}
                    data={mapXsltTemplates(xsltTemplates)}
                    onChange={name => handleXsltDefinitionChange(name)}
                    value=""
                  />
                </Form.Group>
                <Form.Group controlId="xslt-transformation-2">
                  <Stack direction="column" alignItems="stretch" spacing={20}>
                    <Form.ControlLabel>XSLT definition</Form.ControlLabel>
                    <Form.Control style={{ minHeight: 400 }} name="xsltDefinition" accepter={Textarea} />
                    <XMLTransformationPreview
                      url={transformationEdit.xmlUrl}
                      xslt={transformationEdit.xsltDefinition}
                      credentials={
                            mapCredentials(transformationEdit.username, transformationEdit.password)
                        }
                    />
                  </Stack>
                </Form.Group>
                <Form.Group>
                  <Stack justifyContent="flex-end">
                    <ButtonToolbar>
                      <Button appearance="primary" onClick={onSave} disabled={!isValid}>Save</Button>
                      <Link to="/">
                        <Button appearance="default">Cancel</Button>
                      </Link>
                    </ButtonToolbar>
                  </Stack>
                </Form.Group>
              </Form>
            )
      }
      </Panel>
    </PageContainer>
  );
}

export default AddTransformationPage;
